/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { useQuery } from '@tanstack/react-query'
import { MessageCircleHeart } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

import { getTrialFeedbackStats } from './api'

const RATING_META = [
  {
    key: 'beyond_expectations',
    label: 'Beyond expectations',
    emoji: '🤩',
    bar: 'bg-violet-500',
    chip: 'bg-violet-500/10 text-violet-600 dark:text-violet-400',
    ring: 'border-violet-500/20',
  },
  {
    key: 'excellent',
    label: 'Excellent',
    emoji: '😍',
    bar: 'bg-emerald-500',
    chip: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    ring: 'border-emerald-500/20',
  },
  {
    key: 'good',
    label: 'Good',
    emoji: '🙂',
    bar: 'bg-blue-500',
    chip: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
    ring: 'border-blue-500/20',
  },
  {
    key: 'average',
    label: 'Average',
    emoji: '😐',
    bar: 'bg-amber-500',
    chip: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
    ring: 'border-amber-500/20',
  },
  {
    key: 'poor',
    label: 'Poor',
    emoji: '😕',
    bar: 'bg-rose-500',
    chip: 'bg-rose-500/10 text-rose-600 dark:text-rose-400',
    ring: 'border-rose-500/20',
  },
] as const

export function TrialFeedbackStats() {
  const { t } = useTranslation()
  const { data, isLoading } = useQuery({
    queryKey: ['trial-feedback-stats'],
    queryFn: getTrialFeedbackStats,
  })

  const counts = data?.data
  const total = counts
    ? RATING_META.reduce((sum, r) => sum + (counts[r.key] ?? 0), 0)
    : 0

  return (
    <div className='mx-auto max-w-3xl p-4 md:p-6'>
      <div className='mb-6 flex items-center gap-3'>
        <div className='flex size-10 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500/15 to-blue-500/15 text-violet-500'>
          <MessageCircleHeart className='size-5' />
        </div>
        <div>
          <h1 className='text-xl font-semibold tracking-tight md:text-2xl'>
            {t('Trial feedback')}
          </h1>
          <p className='text-muted-foreground text-sm'>
            {t(
              'Ratings visitors gave for the free trial chat, before signing up.'
            )}
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className='grid gap-3 sm:grid-cols-2'>
          {RATING_META.map((r) => (
            <Skeleton key={r.key} className='h-24 w-full rounded-xl' />
          ))}
        </div>
      ) : (
        <>
          <div className='border-border/50 from-muted/40 to-muted/10 mb-4 rounded-xl border bg-gradient-to-br px-5 py-4'>
            <p className='text-muted-foreground text-xs font-medium tracking-wide uppercase'>
              {t('Total responses: {{total}}', { total: '' })}
            </p>
            <p className='mt-0.5 text-3xl font-bold tabular-nums'>{total}</p>
          </div>

          <div className='grid gap-3 sm:grid-cols-2'>
            {RATING_META.map((r) => {
              const count = counts?.[r.key] ?? 0
              const pct = total > 0 ? Math.round((count / total) * 100) : 0
              return (
                <div
                  key={r.key}
                  className={cn(
                    'border-border/50 bg-card relative overflow-hidden rounded-xl border p-4 shadow-sm',
                    r.ring
                  )}
                >
                  <div className='mb-3 flex items-center justify-between'>
                    <div className='flex items-center gap-2.5'>
                      <span
                        className={cn(
                          'flex size-9 items-center justify-center rounded-lg text-lg',
                          r.chip
                        )}
                      >
                        {r.emoji}
                      </span>
                      <span className='text-sm font-semibold'>
                        {t(r.label)}
                      </span>
                    </div>
                    <span className='font-mono text-lg font-bold tabular-nums'>
                      {pct}%
                    </span>
                  </div>
                  <div className='bg-muted h-2 overflow-hidden rounded-full'>
                    <div
                      className={cn(
                        'h-full rounded-full transition-all duration-700 ease-out',
                        r.bar
                      )}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <p className='text-muted-foreground mt-2 text-xs'>
                    {count} {t('votes')}
                  </p>
                </div>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}
